import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './MemoryGame.css';

const EMOJI_SETS = {
  animals: ['🐶', '🐱', '🐼', '🦊', '🐸', '🐵', '🦁', '🐧', '🐙', '🦋', '🐢', '🐬', '🦄', '🐝', '🐨', '🐰', '🦩', '🐺'],
  food: ['🍕', '🍔', '🌮', '🍣', '🍩', '🧁', '🍉', '🍇', '🥑', '🌽', '🍪', '🧀', '🍫', '🍒', '🥝', '🍜', '🥐', '🧇'],
  travel: ['✈️', '🚀', '🏖️', '🗼', '🎢', '⛵', '🏔️', '🌋', '🏰', '🎡', '🚂', '🛸', '🗽', '🌉', '🏝️', '🎪', '⛩️', '🕌'],
};

const GRID_SIZES = [
  { id: '4x3', label: '4×3', cols: 4, rows: 3, pairs: 6 },
  { id: '4x4', label: '4×4', cols: 4, rows: 4, pairs: 8 },
  { id: '6x4', label: '6×4', cols: 6, rows: 4, pairs: 12 },
  { id: '6x6', label: '6×6', cols: 6, rows: 6, pairs: 18 },
];

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function createCards(pairs, theme) {
  const emojis = shuffleArray(EMOJI_SETS[theme]).slice(0, pairs);
  const cards = shuffleArray([...emojis, ...emojis].map((emoji, i) => ({
    id: i,
    emoji,
    flipped: false,
    matched: false,
  })));
  return cards;
}

function MemoryGame() {
  const navigate = useNavigate();
  const [gridSize, setGridSize] = useState(GRID_SIZES[1]);
  const [theme, setTheme] = useState('animals');
  const [cards, setCards] = useState(() => createCards(GRID_SIZES[1].pairs, 'animals'));
  const [flipped, setFlipped] = useState([]);
  const [moves, setMoves] = useState(0);
  const [matches, setMatches] = useState(0);
  const [time, setTime] = useState(0);
  const [running, setRunning] = useState(false);
  const [complete, setComplete] = useState(false);
  const [bestScore, setBestScore] = useState(() => {
    try { return JSON.parse(localStorage.getItem('demohub_memory_best') || '{}'); } catch { return {}; }
  });
  const timerRef = useRef(null);
  const lockRef = useRef(false);

  useEffect(() => {
    if (running && !complete) {
      timerRef.current = setInterval(() => setTime((t) => t + 1), 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [running, complete]);

  const handleNewGame = (gs, th) => {
    const g = gs || gridSize;
    const t = th || theme;
    clearInterval(timerRef.current);
    setCards(createCards(g.pairs, t));
    setFlipped([]);
    setMoves(0);
    setMatches(0);
    setTime(0);
    setRunning(false);
    setComplete(false);
    lockRef.current = false;
  };

  const handleCardClick = (index) => {
    if (lockRef.current || cards[index].flipped || cards[index].matched || flipped.length >= 2) return;
    if (!running) setRunning(true);

    const newCards = cards.map((c, i) => i === index ? { ...c, flipped: true } : c);
    const newFlipped = [...flipped, index];
    setCards(newCards);
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      const newMoves = moves + 1;
      setMoves(newMoves);
      lockRef.current = true;

      if (newCards[newFlipped[0]].emoji === newCards[newFlipped[1]].emoji) {
        setTimeout(() => {
          const matched = newCards.map((c, i) =>
            i === newFlipped[0] || i === newFlipped[1] ? { ...c, matched: true } : c
          );
          setCards(matched);
          setFlipped([]);
          const newMatches = matches + 1;
          setMatches(newMatches);
          lockRef.current = false;

          if (newMatches === gridSize.pairs) {
            setComplete(true);
            clearInterval(timerRef.current);
            const key = gridSize.id;
            const prev = bestScore[key];
            if (!prev || newMoves < prev.moves) {
              const updated = { ...bestScore, [key]: { moves: newMoves, time } };
              setBestScore(updated);
              try { localStorage.setItem('demohub_memory_best', JSON.stringify(updated)); } catch {}
            }
          }
        }, 400);
      } else {
        setTimeout(() => {
          setCards((prev) => prev.map((c, i) =>
            i === newFlipped[0] || i === newFlipped[1] ? { ...c, flipped: false } : c
          ));
          setFlipped([]);
          lockRef.current = false;
        }, 800);
      }
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
  const best = bestScore[gridSize.id];

  return (
    <div className="mem-app">
      <div className="mem-topbar">
        <div className="mem-topbar-left">
          <button className="mem-back-btn" onClick={() => navigate('/demo/games')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="mem-title">Memory Match</h1>
        </div>
        <div className="mem-topbar-right">
          <div className="mem-size-toggle">
            {GRID_SIZES.map((gs) => (
              <button key={gs.id} className={`mem-size-btn ${gridSize.id === gs.id ? 'active' : ''}`}
                onClick={() => { setGridSize(gs); handleNewGame(gs); }}>
                {gs.label}
              </button>
            ))}
          </div>
          <select className="mem-theme-select" value={theme} onChange={(e) => { setTheme(e.target.value); handleNewGame(null, e.target.value); }}>
            <option value="animals">🐶 Animals</option>
            <option value="food">🍕 Food</option>
            <option value="travel">✈️ Travel</option>
          </select>
          <button className="mem-action-btn new" onClick={() => handleNewGame()}>New Game</button>
        </div>
      </div>

      <div className="mem-container">
        <div className="mem-stats">
          <div className="mem-stat"><span className="mem-stat-val">{moves}</span><span className="mem-stat-lbl">Moves</span></div>
          <div className="mem-stat"><span className="mem-stat-val">{matches}/{gridSize.pairs}</span><span className="mem-stat-lbl">Pairs</span></div>
          <div className="mem-stat"><span className="mem-stat-val">{formatTime(time)}</span><span className="mem-stat-lbl">Time</span></div>
          <div className="mem-stat"><span className="mem-stat-val">{best ? best.moves : '—'}</span><span className="mem-stat-lbl">Best</span></div>
        </div>

        <div className="mem-board" style={{ '--cols': gridSize.cols, '--rows': gridSize.rows }}>
          {cards.map((card, i) => (
            <div
              key={card.id}
              className={`mem-card ${card.flipped || card.matched ? 'flipped' : ''} ${card.matched ? 'matched' : ''}`}
              onClick={() => handleCardClick(i)}
            >
              <div className="mem-card-inner">
                <div className="mem-card-front">?</div>
                <div className="mem-card-back">{card.emoji}</div>
              </div>
            </div>
          ))}
        </div>

        {complete && (
          <div className="mem-complete">
            <span className="mem-complete-icon">🎉</span>
            <h2>Congratulations!</h2>
            <p>Completed in <strong>{moves} moves</strong> and <strong>{formatTime(time)}</strong></p>
            <button className="mem-play-again" onClick={() => handleNewGame()}>Play Again</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default MemoryGame;
